import type { InitializeProcessInput } from '@bliks-io/contracts';
import {
  createSystemRowSchema,
  createUserSchema,
  type MembershipRowInput,
  type SystemRowInput,
  type UserInput
} from '@/lib/excel';
import { createInitializeProcessSchema } from '@/components/bulk-upload';
import { zodRowValidator, type RowValidator } from '@/lib/draft-rows/draftRow';
import type { CrossRowCheck } from '@/lib/draft-rows/markDuplicateRows';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';
import { createMemberEmailAwareRowValidator } from './orgFunctionRowValidation';
import { createDepartmentSchema, type DepartmentInput } from './schemas';

// Draft rows stay unvalidated while typing (manual adds are neutral); Weiter
// runs the authoritative pass with the same schemas the parsers use.
export const validateDepartmentRow = zodRowValidator<DepartmentInput>(createDepartmentSchema);
export const validateUserRow = zodRowValidator<UserInput>(createUserSchema);

// Systems/Processes owner emails must resolve to a user, exactly like
// Leitung/membership emails on the Funktionen/Teams surface — otherwise an
// unknown owner only fails at the server (400 after Weiter). The email set
// must cover EVERY user the backend resolves owners against (any status, any
// classification), or a seeded row the server accepts blocks the step.
export function createOwnerAwareSystemRowValidator(
  memberEmails: Set<string>
): RowValidator<SystemRowInput> {
  return createMemberEmailAwareRowValidator(createSystemRowSchema, ['ownerEmail'], memberEmails);
}

export function createOwnerAwareProcessRowValidator(
  memberEmails: Set<string>
): RowValidator<InitializeProcessInput> {
  return createMemberEmailAwareRowValidator(
    createInitializeProcessSchema,
    ['ownerEmail', 'accountableEmail'],
    memberEmails
  );
}

// Each step's natural key — the same identity its import dedupes/skips by.
// camelCase on purpose: check-translations harvests UPPER_CASE string arrays
// as template-expansion values, so an UPPER_CASE name here would poison
// unrelated t(`...${...}`) templates with 'name'/'email' pseudo-keys.
export const departmentDuplicateKeys = [
  { field: 'name' as const },
  { field: 'abbreviation' as const }
];
export const userDuplicateKeys = [{ field: 'email' as const }];

/**
 * Draft rows may not collide with members the table cannot represent —
 * externals and out-of-scope statuses (e.g. suspended internals) never seed a
 * row here, so the plain duplicate-email pass cannot see them. Unflagged, the
 * backend would skip the row idempotently (created:false), the collision
 * follow-up would re-send it as an update, and the update is rejected for
 * non-wizard users (INITIAL_SETUP_UPDATE_NOT_ALLOWED) — a partial commit plus
 * a permanently wedged autosave retry. Representable members (editable seeds
 * and protected rows) are excluded from the email set by construction, so
 * their own rows never flag.
 */
export function createUnrepresentedMemberCheck(
  unrepresentedEmails: Set<string>
): CrossRowCheck<UserInput> {
  return {
    field: 'email',
    findViolations: (rows) =>
      new Set(
        rows.flatMap((row, index) =>
          unrepresentedEmails.has(normalizedKey(row.email)) ? [index] : []
        )
      ),
    messageKey: 'bulkOnboarding.validation.existingMember'
  };
}

/**
 * A new draft row naming a Stelle that already exists on the backend (and is
 * carried by no seeded row, so its current Beschreibung is invisible here)
 * would silently overwrite that description — surface it instead. Seeded
 * rows are exempt by construction: their roles are excluded from the map,
 * since editing a visible description IS the designed update path.
 */
export function createUnseenRoleDescriptionCheck(
  existingDescriptions: Map<string, string>
): CrossRowCheck<UserInput> {
  return {
    field: 'roleDescription',
    findViolations: (rows) =>
      new Set(
        rows.flatMap((row, index) => {
          const existing = existingDescriptions.get(normalizedKey(row.role));
          const entered = row.roleDescription?.trim();
          return existing && entered && entered !== existing ? [index] : [];
        })
      ),
    messageKey: 'bulkOnboarding.validation.roleDescriptionConflict'
  };
}

/**
 * A parent chain that leads back to the row itself — directly (own
 * abbreviation as parent) or through other rows. The backend tolerates this
 * by silently dropping the cycle; an inline error is honest instead.
 */
function findCircularParentRows(rows: readonly DepartmentInput[]): Set<number> {
  const indexByAbbreviation = new Map<string, number>();
  rows.forEach((row, index) => {
    const abbreviation = normalizedKey(row.abbreviation);
    if (abbreviation && !indexByAbbreviation.has(abbreviation)) {
      indexByAbbreviation.set(abbreviation, index);
    }
  });

  const violations = new Set<number>();
  rows.forEach((row, start) => {
    const visited = new Set<number>([start]);
    let current = row;
    for (;;) {
      const parentIndex = indexByAbbreviation.get(normalizedKey(current.parentAbbr));
      if (parentIndex === undefined || visited.has(parentIndex)) {
        if (parentIndex === start) violations.add(start);
        return;
      }
      visited.add(parentIndex);
      current = rows[parentIndex];
    }
  });
  return violations;
}

export const departmentCrossRowChecks: CrossRowCheck<DepartmentInput>[] = [
  {
    field: 'parentAbbr',
    findViolations: findCircularParentRows,
    messageKey: 'bulkOnboarding.validation.circularParent'
  }
];

/**
 * Rows sharing one Stelle share one OrgFunction, so their descriptions
 * collapse to a single value on the backend — conflicting non-empty
 * descriptions must be reconciled before Weiter instead of silently
 * first-one-wins. Only reachable via Excel import; manual edits live-sync.
 */
function findRoleDescriptionConflicts(rows: readonly UserInput[]): Set<number> {
  const rowsByRole = new Map<string, number[]>();
  rows.forEach((row, index) => {
    const role = normalizedKey(row.role);
    if (!role) return;
    const group = rowsByRole.get(role);
    if (group) group.push(index);
    else rowsByRole.set(role, [index]);
  });

  const violations = new Set<number>();
  for (const group of rowsByRole.values()) {
    const descriptions = new Set(
      group.map((index) => rows[index].roleDescription?.trim()).filter(Boolean)
    );
    if (descriptions.size <= 1) continue;
    for (const index of group) {
      if (rows[index].roleDescription?.trim()) violations.add(index);
    }
  }
  return violations;
}

export const userCrossRowChecks: CrossRowCheck<UserInput>[] = [
  {
    field: 'roleDescription',
    findViolations: findRoleDescriptionConflicts,
    messageKey: 'bulkOnboarding.validation.roleDescriptionConflict'
  }
];
export const orgFunctionDuplicateKeys = [{ field: 'name' as const }];
// Composite keys go blank when EITHER part is blank — a truthy partial key
// like 'vertrieb#' would let an incomplete uploaded row upsert-replace a
// half-filled manual row. Both parts are schema-required, so incomplete rows
// still fail row validation on Weiter.
export const membershipDuplicateKeys = [
  {
    field: 'memberEmail' as const,
    keyOf: (data: MembershipRowInput) =>
      data.orgFunctionName && data.memberEmail
        ? `${data.orgFunctionName}#${data.memberEmail}`
        : undefined
  }
];
export const systemDuplicateKeys = [{ field: 'name' as const }];
export const processDuplicateKeys = [
  {
    field: 'name' as const,
    keyOf: (data: InitializeProcessInput) =>
      data.name && data.department ? `${data.name}#${data.department}` : undefined
  }
];
